import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';

import { ALL_OPTION_KEY, RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';

import { useGetEmployerIdParam } from '../../hooks';
import { useBaseReportTableQuery } from '../../queries';
import { PayrollContributionReportTableParams } from '../PayrollContributionReport.types';

export const QUERY_KEY = 'PAYROLL_CONTRIBUTION_REPORT_QUERY';

const MIN_SEARCH_LEN = 3;
const usePayrollContributionReportQuery = (params: Partial<PayrollContributionReportTableParams>) => {
  const formattedStartDate = useMemo(() => {
    if (!params.startDate) {
      return null;
    }
    const parsedDate = dayjs(params.startDate);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format(RAW_DATE_FORMAT);
  }, [params.startDate]);

  const formattedEndDate = useMemo(() => {
    if (!params.endDate) {
      return null;
    }
    const parsedDate = dayjs(params.endDate);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format(RAW_DATE_FORMAT);
  }, [params.endDate]);

  const contributionStartDate = useMemo(() => {
    return params.isContributionMode ? formattedStartDate : null;
  }, [formattedStartDate, params.isContributionMode]);

  const contributionEndDate = useMemo(() => {
    return params.isContributionMode ? formattedEndDate : null;
  }, [formattedEndDate, params.isContributionMode]);

  const transactionStartDate = useMemo(() => {
    return params.isContributionMode ? null : formattedStartDate;
  }, [formattedStartDate, params.isContributionMode]);
  const transactionEndDate = useMemo(() => {
    return params.isContributionMode ? null : formattedEndDate;
  }, [formattedEndDate, params.isContributionMode]);

  const currentSearch = useMemo(() => {
    if (!!params?.searchString && params?.searchString?.length >= MIN_SEARCH_LEN) {
      return params.searchString;
    }
    return null;
  }, [params.searchString]);
  const getEmployerIdParam = useGetEmployerIdParam();
  const employerIdsString = useMemo(() => params?.employerIds?.join(','), [params?.employerIds]);
  const base = useBaseReportTableQuery(
    API_URL.REPORTS_PAYROLL_CONTRIBUTIONS_REPORT_BREAKDOWN,
    [
      QUERY_KEY,
      contributionStartDate,
      contributionEndDate,
      transactionEndDate,
      transactionStartDate,
      params?.reportPlanIds?.join(','),
      currentSearch,
      params.employerId,
      employerIdsString,
    ],
    {
      ...getEmployerIdParam(params.employerId, params.employerIds),
      show_ssn: true,
      isSSNShowed: params.isSSNShowed,
      contribution_start_date: contributionStartDate,
      contribution_end_date: contributionEndDate,
      transaction_start_date: transactionStartDate,
      transaction_end_date: transactionEndDate,
      search_value: currentSearch,
      plan_ids:
        params?.reportPlanIds && !params?.reportPlanIds.includes(ALL_OPTION_KEY)
          ? params?.reportPlanIds.join(',')
          : undefined,
    },
    {
      isMultipleEmployers: params.isMultipleEmployer,
      isPreviewMode: params.isPreviewMode,
    }
  );

  useEffect(() => {
    base.onChangePlanOptions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.employerId, employerIdsString]);

  useEffect(() => {
    base.setPage(0);
    base.setRawContentList([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    params.employerId,
    params?.startDate,
    params?.endDate,
    params?.reportPlanIds,
    params?.searchString,
    params.employerId,
    params?.isContributionMode,
    employerIdsString,
  ]);
  return base;
};

export default usePayrollContributionReportQuery;
